import React from 'react';
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CancelIcon from "@mui/icons-material/Cancel";
import { Checkbox } from "@mui/material";
import "./DropdownSelector.css";

// Base interface for dropdown options
interface BaseOption {
  id: string;
  label: string;
}

// Extended interface for invoice options with additional data
interface InvoiceOption extends BaseOption {
  paymentStatus?: string;
  statusStyling?: {
    dmsLabelColor: string;
    dmsLabel: string;
    dmsColor: string;
  };
}

// Props interface for the component
interface DropdownSelectorProps {
  label: string;
  placeholder: string;
  options: BaseOption[] | InvoiceOption[];
  isOpen: boolean;
  onToggle: () => void;
  selectedValue?: BaseOption | InvoiceOption | null;
  onSingleSelect?: (option: BaseOption | InvoiceOption) => void;
  onRemoveSingle?: (event: React.MouseEvent) => void;
  selectedValues?: string[];
  onMultiSelect?: (optionId: string) => void;
  onRemoveMulti?: (optionId: string, event: React.MouseEvent) => void;
  multiSelect?: boolean;
  showCustomRadio?: boolean;
  required?: boolean;
  singleSelectTagStyle?: React.CSSProperties;
  multiSelectTagStyle?: React.CSSProperties;
}

const DropdownSelector: React.FC<DropdownSelectorProps> = ({
  label,
  placeholder,
  options,
  isOpen,
  onToggle,
  selectedValue,
  onSingleSelect,
  onRemoveSingle,
  selectedValues = [],
  onMultiSelect,
  onRemoveMulti,
  multiSelect = false,
  showCustomRadio = false,
  required = false,
  singleSelectTagStyle,
  multiSelectTagStyle
}) => {
  // Check if an option is selected (for multi-select)
  const isOptionSelected = (optionId: string): boolean => {
    if (!optionId || !selectedValues) return false;
    return selectedValues.includes(optionId);
  };

  // Handle option click
  const handleOptionClick = (option: BaseOption | InvoiceOption): void => {
    if (!option) return;

    if (multiSelect && onMultiSelect) {
      onMultiSelect(option.id);
    } else if (!multiSelect && onSingleSelect) {
      onSingleSelect(option);
    }
  };

  // Render selected tags for multi-select
  const renderMultiSelectTags = () => {
    if (!selectedValues || !Array.isArray(selectedValues)) return null; // Add null check for selectedValues

    return selectedValues.map((valueId: string, index: number) => {
      if (!valueId) return null; // Add null check for valueId

      const option = options?.find(opt => opt?.id === valueId);
      if (!option) return null;

      return (
        <div
          key={index}
          className="dropdown_selectTag_selectedTag"
          style={multiSelectTagStyle}
        >
          {option.label}
          <CancelIcon
            sx={{ color: multiSelectTagStyle?.color || '#16B4A3', fontSize: '13px' }}
            onClick={(event) => onRemoveMulti?.(valueId, event)}
          />
        </div>
      );
    });
  };

  // Render single select tag
  const renderSingleSelectTag = () => {
    if (!selectedValue || !selectedValue.label) return null;

    return (
      <div
        className="dropdown_selectTag_selectedTag"
        style={singleSelectTagStyle}
      >
        {selectedValue.label}
        <CancelIcon
          sx={{ color: singleSelectTagStyle?.color || '#1E40CC', fontSize: '13px' }}
          onClick={(event) => onRemoveSingle?.(event)} // Ensure event is passed
        />
      </div>
    );
  };

  // Render option content
  const renderOptionContent = (option: BaseOption | InvoiceOption) => {
    const invoiceOption = option as InvoiceOption;

    return (
      <>
        {/* Radio button or checkbox */}
        {multiSelect ? (
          <Checkbox
            checked={isOptionSelected(option.id)}
            sx={{
              "& .MuiSvgIcon-root": {
                "& path": {
                  strokeWidth: 1,
                },
              },
            }}
          />
        ) : showCustomRadio ? (
          <div
            className="dropdown_selectTag_radioButton"
            style={
              selectedValue?.id === option.id
                ? {
                  backgroundColor: "#007bff",
                  borderColor: "#007bff",
                  position: "relative",
                }
                : {}
            }
          >
            {selectedValue?.id === option.id && (
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "6px",
                  height: "6px",
                  backgroundColor: "white",
                  borderRadius: "50%",
                }}
              />
            )}
          </div>
        ) : null}

        {/* Option label */}
        <span className="dropdown_selectTag_options_text">
          {option.label}
        </span>

        {/* Payment status (for invoice options) */}
        {invoiceOption.statusStyling && (
          <div
            className="dropdown_selectTag_paymentStatus"
            style={{
              background: invoiceOption.statusStyling.dmsColor,
              color: invoiceOption.statusStyling.dmsLabelColor,
            }}
          >
            {invoiceOption.statusStyling.dmsLabel}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="dropdown_selectInvoiceContainer">
      <p className="dropdown_label">
        {label}
        {required && <span>*</span>}
      </p>
      <div className="dropdown_selectTagContainer">
        <div
          className="dropdown_selectTag_header"
          style={
            isOpen
              ? {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
              : {}
          }
          onClick={onToggle}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flex: 1,
            }}
          >
            {/* Render selected content */}
            {multiSelect ? (
              selectedValues.length > 0 ? (
                renderMultiSelectTags()
              ) : (
                <span className="dropdown_selectTag_header_text">
                  {placeholder}
                </span>
              )
            ) : (
              selectedValue ? (
                renderSingleSelectTag()
              ) : (
                <span className="dropdown_selectTag_header_text">
                  {placeholder}
                </span>
              )
            )}
          </div>
          <ArrowDropDownIcon
            sx={{
              color: "#6E6969",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease"
            }}
          />
        </div>

        {/* Dropdown options */}
        {isOpen && (
          <div
            className="dropdown_selectTag_body"
            style={multiSelect ? { gap: "4px" } : {}}
          >
            {options.map((option: BaseOption | InvoiceOption, index: number) => (
              <div
                key={index}
                className="dropdown_selectTag_options"
                onClick={() => handleOptionClick(option)}
              >
                {renderOptionContent(option)}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DropdownSelector;